import React, { useMemo, useState } from 'react';
import { ArrowUpCircle, ArrowDownCircle, Edit, Trash2, Shield, History, X, Search, Filter, Package } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatCurrency, formatDate, formatNumber } from '../../lib/utils';

export function MovementsSheet({
  movements = [],
  materials = [],
  exchangeRate,
  isSuperUser,
  onEdit,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');       // all | entrada | salida
  const [materialFilter, setMaterialFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Helper para peso equivalente
  const getWeightEquivalent = (movement) => {
    const baseUnit = (movement.base_unit || '').toLowerCase();
    if (!['saco', 'sacos'].includes(baseUnit)) return null;
    let sackWeight = movement.sack_weight;
    let sackUnit = movement.sack_weight_unit;
    if (!sackWeight) {
      const mat = materials.find(m => m.id === movement.material_id);
      sackWeight = mat?.sack_weight;
      sackUnit = mat?.sack_weight_unit;
    }
    if (!sackWeight || sackWeight <= 0) return null;
    return { totalWeight: (movement.quantity_base || 0) * sackWeight, sackWeight, sackUnit: sackUnit || 'kg' };
  };

  // Filtrado
  const filtered = useMemo(() => {
    return movements.filter(m => {
      if (typeFilter !== 'all' && m.type !== typeFilter) return false;
      if (materialFilter !== 'all' && m.material_id !== materialFilter) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const inName = (m.material_name || '').toLowerCase().includes(q);
        const inNotes = (m.notes || '').toLowerCase().includes(q);
        const inSupplier = (m.supplier || '').toLowerCase().includes(q);
        if (!inName && !inNotes && !inSupplier) return false;
      }
      const movDate = m.purchase_date || (m.created_at ? m.created_at.split('T')[0] : '');
      if (dateFrom && movDate < dateFrom) return false;
      if (dateTo && movDate > dateTo) return false;
      return true;
    });
  }, [movements, typeFilter, materialFilter, searchText, dateFrom, dateTo]);

  // Stats por filtro
  const stats = useMemo(() => {
    const entradas = filtered.filter(m => m.type === 'entrada').length;
    const salidas = filtered.filter(m => m.type === 'salida').length;
    return { total: filtered.length, entradas, salidas };
  }, [filtered]);

  const resetFilters = () => {
    setTypeFilter('all');
    setMaterialFilter('all');
    setSearchText('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = typeFilter !== 'all' || materialFilter !== 'all' || searchText || dateFrom || dateTo;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="w-full sm:w-auto border-2 border-[#931A25]/20 hover:border-[#931A25]/40 hover:bg-[#931A25]/5 text-[#931A25] font-medium gap-2"
          data-testid="open-movements-sheet-btn"
        >
          <History className="h-4 w-4" />
          Movimientos Recientes
          {movements.length > 0 && (
            <Badge variant="secondary" className="ml-1 bg-[#931A25]/10 text-[#931A25] hover:bg-[#931A25]/20">
              {movements.length}
            </Badge>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto p-0" data-testid="movements-sheet-content">
        {open && (
        <>
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-[#931A25] to-[#B32234] text-white p-6 sticky top-0 z-20 shadow-md">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="text-white flex items-center gap-2 text-2xl">
                  <History className="h-6 w-6" />
                  Historial de Movimientos
                </SheetTitle>
                <SheetDescription className="text-white/80 mt-1">
                  Compras y salidas del inventario con filtros avanzados
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Stats rápidas */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 border border-white/20">
              <p className="text-xs text-white/70">Total mostrado</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 border border-white/20">
              <p className="text-xs text-white/70 flex items-center gap-1"><ArrowUpCircle className="h-3 w-3" /> Compras</p>
              <p className="text-2xl font-bold text-green-200">{stats.entradas}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 border border-white/20">
              <p className="text-xs text-white/70 flex items-center gap-1"><ArrowDownCircle className="h-3 w-3" /> Salidas</p>
              <p className="text-2xl font-bold text-red-200">{stats.salidas}</p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 sticky top-[188px] z-10">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-700">Filtros</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto h-7 text-xs text-slate-500 hover:text-[#931A25]">
                <X className="h-3 w-3 mr-1" /> Limpiar
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
            {/* Buscador */}
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar material, nota o proveedor..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-8 bg-white"
                data-testid="movements-search-input"
              />
            </div>

            {/* Tipo */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="bg-white" data-testid="movements-type-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="entrada">
                  <span className="flex items-center gap-2"><ArrowUpCircle className="h-3 w-3 text-green-600" /> Compras</span>
                </SelectItem>
                <SelectItem value="salida">
                  <span className="flex items-center gap-2"><ArrowDownCircle className="h-3 w-3 text-red-600" /> Salidas</span>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Material */}
            <Select value={materialFilter} onValueChange={setMaterialFilter}>
              <SelectTrigger className="bg-white" data-testid="movements-material-filter">
                <SelectValue placeholder="Material" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Todos los materiales</SelectItem>
                {materials.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Reset como quinta col cuando no hay activos */}
            {!hasActiveFilters && (
              <div className="text-xs text-slate-400 flex items-center px-2">
                Sin filtros activos
              </div>
            )}
          </div>

          {/* Rango de fechas */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Desde</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-white h-9"
                data-testid="movements-date-from"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Hasta</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-white h-9"
                data-testid="movements-date-to"
              />
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="p-4">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Package className="h-12 w-12 mb-3" />
              <p className="font-medium">No hay movimientos que coincidan con los filtros</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={resetFilters} className="mt-2 text-[#931A25]">
                  Limpiar filtros
                </Button>
              )}
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="w-[100px]">Fecha</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead className="text-center w-[110px]">Tipo</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Costo Unit.</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="text-center w-[80px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(movement => (
                    <TableRow key={movement.id} className={`hover:bg-slate-50 ${movement.type === 'salida' ? 'bg-red-50/30' : ''}`}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(movement.purchase_date || movement.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div>
                          {movement.material_name}
                          {movement.type === 'salida' && movement.notes && (
                            <p className="text-[10px] text-slate-500 mt-0.5 max-w-[240px] truncate" title={movement.notes}>
                              {movement.notes}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {movement.type === 'entrada' ? (
                          <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
                            <ArrowUpCircle className="h-3 w-3 mr-1" /> Compra
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
                            <ArrowDownCircle className="h-3 w-3 mr-1" /> Salida
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div>
                          <span className="text-slate-700">
                            {formatNumber(movement.quantity)} {movement.input_unit || movement.unit || ''}
                          </span>
                          {movement.quantity_base && movement.base_unit !== movement.input_unit && (
                            <div className="text-xs text-blue-700 font-medium">
                              = {formatNumber(movement.quantity_base)} {movement.base_unit}
                            </div>
                          )}
                          {(() => {
                            const w = getWeightEquivalent(movement);
                            return w ? (
                              <div className="text-[10px] text-slate-400">
                                ({formatNumber(w.totalWeight)} {w.sackUnit})
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {movement.unit_cost ? (
                          <div>
                            <div className="text-sm">{formatCurrency(movement.unit_cost, movement.currency || 'DOP')}/{movement.input_unit || movement.unit}</div>
                            {movement.currency === 'USD' && (
                              <div className="text-xs text-green-600">
                                (RD$ {(movement.unit_cost * exchangeRate).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})})
                              </div>
                            )}
                          </div>
                        ) : movement.type === 'salida' ? (
                          <span className="text-xs text-slate-400">Despacho</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {movement.supplier || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {movement.type === 'entrada' ? (
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEdit(movement)}
                              className={`hover:bg-blue-50 h-8 w-8 p-0 ${isSuperUser ? 'text-blue-600' : 'text-slate-400'}`}
                              data-testid={`edit-movement-${movement.id}`}
                              title={isSuperUser ? 'Editar' : 'Requiere Super Usuario'}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(movement.id)}
                              className={`hover:bg-red-50 h-8 w-8 p-0 ${isSuperUser ? 'text-red-600' : 'text-slate-400'}`}
                              data-testid={`delete-movement-${movement.id}`}
                              title={isSuperUser ? 'Eliminar' : 'Requiere Super Usuario'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Auto</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        </>
        )}
      </SheetContent>
    </Sheet>
  );
}
